import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  userId: string;
  email: string;
  displayName: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  setAuth: (token: string, user: User) => void;
  clearAuth: () => void;
  initializeAuth: () => void;
}

/**
 * Global authentication store using Zustand
 * Persists auth state to localStorage
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isInitialized: false,

      /**
       * Set authentication state after login/register
       */
      setAuth: (token: string, user: User) => {
        // Store token in localStorage for API client compatibility
        localStorage.setItem('auth_token', token);
        
        set({
          token,
          user,
          isAuthenticated: true,
          isInitialized: true,
        });
      },

      /**
       * Clear authentication state on logout
       */
      clearAuth: () => {
        // Remove token from localStorage
        localStorage.removeItem('auth_token');
        
        set({
          token: null,
          user: null,
          isAuthenticated: false,
          isInitialized: true,
        });
      },

      /**
       * Initialize auth state from localStorage on app load
       * This syncs the Zustand persist state with the auth_token in localStorage
       */
      initializeAuth: () => {
        const token = localStorage.getItem('auth_token');
        const storedState = localStorage.getItem('auth-storage');
        
        if (token && storedState) {
          try {
            const parsed = JSON.parse(storedState);
            if (parsed.state?.user) {
              set({
                token,
                user: parsed.state.user,
                isAuthenticated: true,
                isInitialized: true,
              });
              return;
            }
          } catch (error) {
            console.error('Failed to parse stored auth state:', error);
            // Clear invalid state
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth-storage');
          }
        }
        
        // Mark as initialized even if no stored auth
        set({ isInitialized: true });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
